import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ApiServiceV2 from 'services/apiService/apiServiceV2';
import { Checkbox, LogoIcon } from 'components';
import useTheme from 'hooks/useTheme';

const AssignmentsContainer = styled.section`
	--assignments-accent: ${({ $newTheme }) => $newTheme._secondaryColor};
	--assignments-background: ${({ $newTheme }) =>
		$newTheme._primaryBackgroundColor};
	--assignments-surface: ${({ $newTheme }) =>
		$newTheme._secondaryBackgroundColor};
	--assignments-highlight: ${({ $newTheme }) =>
		$newTheme._surfaceBackgroundColor};
	--assignments-border: ${({ $newTheme }) => $newTheme._surfaceBackgroundColor};
	--assignments-text: ${({ $newTheme }) => $newTheme._highEmphasisColor};
	--assignments-muted: ${({ $newTheme }) => $newTheme._mediumEmphasisColor};

	width: 100%;
	padding: 14px;
	border: 1px solid var(--assignments-border);
	border-radius: 8px;
	background: var(--assignments-background);
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
	box-sizing: border-box;
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;
`;

const SectionHeading = styled.div`
	min-width: 0;
`;

const SectionTitle = styled.h3`
	margin: 0;
	color: var(--assignments-text);
	font-size: 16px;
	font-weight: 600;
	line-height: 1.35;
`;

const SectionDescription = styled.p`
	margin: 4px 0 0;
	color: var(--assignments-muted);
	font-size: 13px;
	line-height: 1.45;
`;

const SelectionCount = styled.span`
	flex: none;
	padding: 4px 9px;
	border: 1px solid var(--assignments-border);
	border-radius: 999px;
	background: var(--assignments-highlight);
	color: var(--assignments-text);
	font-size: 12px;
	font-weight: 600;
	line-height: 1.3;
`;

const ProjectsViewport = styled.div`
	max-height: min(42vh, 420px);
	overflow-y: auto;
	overscroll-behavior: contain;
	padding: 1px 5px 1px 1px;
	scrollbar-gutter: stable;

	&:focus-visible {
		outline: 2px solid var(--assignments-accent);
		outline-offset: 2px;
	}

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-thumb {
		border: 2px solid transparent;
		border-radius: 999px;
		background: var(--assignments-highlight);
		background-clip: padding-box;
	}
`;

const ProjectsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
	align-items: start;
	gap: 10px;
`;

const ProjectCard = styled.fieldset`
	min-width: 0;
	margin: 0;
	padding: 10px;
	border: 1px solid var(--assignments-border);
	border-radius: 7px;
	background: var(--assignments-surface);
`;

const ProjectLegend = styled.legend`
	width: 100%;
	padding: 0;
	color: var(--assignments-text);
	font-size: 14px;
	font-weight: 600;
	line-height: 1.35;
`;

const BusinessLines = styled.div`
	display: grid;
	gap: 5px;
	margin-top: 7px;
`;

const BusinessLineOption = styled(Checkbox)`
	display: flex;
	align-items: center;
	width: 100%;
	min-height: 40px;
	margin: 0;
	padding: 6px 9px;
	border: 1px solid
		${({ $selected }) =>
			$selected ? 'var(--assignments-accent)' : 'transparent'};
	border-radius: 6px;
	background: ${({ $selected }) =>
		$selected ? 'var(--assignments-highlight)' : 'transparent'};
	color: var(--assignments-text);
	cursor: pointer;
	transition:
		background-color 120ms ease,
		border-color 120ms ease,
		box-shadow 120ms ease;
	box-sizing: border-box;

	&:not(.ant-checkbox-wrapper-disabled):hover {
		background: var(--assignments-highlight);
	}

	&:focus-within {
		outline: 2px solid var(--assignments-accent);
		outline-offset: 2px;
	}

	&.ant-checkbox-wrapper-disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}

	.ant-checkbox + span {
		flex: 1;
		min-width: 0;
		padding-right: 0;
	}

	.ant-checkbox-checked .ant-checkbox-inner {
		border-color: var(--assignments-accent);
		background-color: var(--assignments-accent);
	}
`;

const StatusMessage = styled.p`
	margin: 4px 0 0;
	padding: 14px;
	border-radius: 6px;
	background: var(--assignments-surface);
	color: var(--assignments-muted);
	font-size: 13px;
	line-height: 1.45;
`;

const ProjectStatus = styled.p`
	margin: 10px 0 0;
	padding: 8px 9px;
	border-radius: 6px;
	background: var(--assignments-highlight);
	color: var(--assignments-muted);
	font-size: 12px;
	line-height: 1.4;
`;

const LoadingStatus = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 52px;
`;

const ScreenReaderOnly = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const hasAssignment = (assignments, projectId, orderTargetId) =>
	assignments.some(
		assignment =>
			String(assignment.projectId) === String(projectId) &&
			String(assignment.orderTargetId) === String(orderTargetId),
	);

const ContractorProjectAssignments = ({
	value,
	values,
	setValues,
	disabled,
}) => {
	const { theme } = useTheme();
	const [projects, setProjects] = useState([]);
	const [isFetching, setIsFetching] = useState(false);
	const moduleItemId = values?.selectedModuleItemId;
	const previousModuleItemId = useRef();
	const assignments = Array.isArray(value) ? value : [];

	useEffect(() => {
		let isMounted = true;
		const moduleChanged =
			previousModuleItemId.current &&
			previousModuleItemId.current !== moduleItemId;

		if (moduleChanged) {
			setValues(previousValues => {
				const hasValuesToClear =
					previousValues.projectAssignments?.length ||
					previousValues.selectedProjectId ||
					previousValues.selectedOrderTargetFilterId;
				if (!hasValuesToClear) return previousValues;

				return {
					...previousValues,
					projectAssignments: [],
					selectedProjectId: undefined,
					selectedOrderTargetFilterId: undefined,
				};
			});
		}
		previousModuleItemId.current = moduleItemId;

		const getProjectsWithBusinessLines = async () => {
			if (!moduleItemId) {
				setProjects([]);
				setIsFetching(false);
				return;
			}

			setIsFetching(true);
			try {
				const projectData = await ApiServiceV2.get({
					url: `/departments/v1/projects/${moduleItemId}`,
				});
				const projectsWithBusinessLines = await Promise.all(
					projectData.map(async project => {
						try {
							const orderTargets = await ApiServiceV2.get({
								url: `/modules/v1/orderTargets/${moduleItemId}/${project.id}`,
							});

							return {
								...project,
								orderTargets: Array.isArray(orderTargets) ? orderTargets : [],
								orderTargetsLoadFailed: false,
							};
						} catch (_) {
							return {
								...project,
								orderTargets: [],
								orderTargetsLoadFailed: true,
							};
						}
					}),
				);
				if (isMounted) setProjects(projectsWithBusinessLines);
			} catch (_) {
				if (isMounted) setProjects([]);
			} finally {
				if (isMounted) setIsFetching(false);
			}
		};

		getProjectsWithBusinessLines();
		return () => {
			isMounted = false;
		};
	}, [moduleItemId]);

	const toggleAssignment = (projectId, orderTargetId) => {
		setValues(previousValues => {
			const previousAssignments = Array.isArray(
				previousValues.projectAssignments,
			)
				? previousValues.projectAssignments
				: [];
			const nextAssignments = hasAssignment(
				previousAssignments,
				projectId,
				orderTargetId,
			)
				? previousAssignments.filter(
						assignment =>
							String(assignment.projectId) !== String(projectId) ||
							String(assignment.orderTargetId) !== String(orderTargetId),
					)
				: previousAssignments.concat({ projectId, orderTargetId });
			const firstAssignment = nextAssignments[0];

			return {
				...previousValues,
				projectAssignments: nextAssignments,
				// Keep the existing single-pair contract for consumers that have not
				// migrated to projectAssignments yet.
				selectedProjectId: firstAssignment?.projectId,
				selectedOrderTargetFilterId: firstAssignment?.orderTargetId,
			};
		});
	};

	return (
		<AssignmentsContainer
			$newTheme={theme}
			aria-labelledby='contractor-project-assignments-title'
			aria-describedby='contractor-project-assignments-description'
		>
			<SectionHeader>
				<SectionHeading>
					<SectionTitle id='contractor-project-assignments-title'>
						Proyecto y línea de negocio
					</SectionTitle>
					<SectionDescription id='contractor-project-assignments-description'>
						Seleccione una o más combinaciones para la nueva contrata.
					</SectionDescription>
				</SectionHeading>
				{assignments.length > 0 && (
					<SelectionCount aria-live='polite'>
						{assignments.length}{' '}
						{assignments.length === 1 ? 'seleccionada' : 'seleccionadas'}
					</SelectionCount>
				)}
			</SectionHeader>
			{isFetching && (
				<LoadingStatus role='status' aria-live='polite'>
					<LogoIcon spin={true} />
					<ScreenReaderOnly>
						Cargando proyectos y líneas de negocio.
					</ScreenReaderOnly>
				</LoadingStatus>
			)}
			{!isFetching && projects.length > 0 && (
				<ProjectsViewport tabIndex={0} aria-label='Proyectos disponibles'>
					<ProjectsGrid>
						{projects.map(project => (
							<ProjectCard key={project.id}>
								<ProjectLegend>{project.name}</ProjectLegend>
								{project.orderTargets.length > 0 && (
									<BusinessLines>
										{project.orderTargets.map(orderTarget => {
											const selected = hasAssignment(
												assignments,
												project.id,
												orderTarget.id,
											);

											return (
												<BusinessLineOption
													key={`${project.id}-${orderTarget.id}`}
													$selected={selected}
													checked={selected}
													disabled={disabled}
													onChange={() =>
														toggleAssignment(project.id, orderTarget.id)
													}
												>
													{orderTarget.name}
												</BusinessLineOption>
											);
										})}
									</BusinessLines>
								)}
								{project.orderTargets.length === 0 && (
									<ProjectStatus
										role={project.orderTargetsLoadFailed ? 'status' : undefined}
									>
										{project.orderTargetsLoadFailed
											? 'No fue posible cargar sus líneas de negocio.'
											: 'No tiene líneas de negocio disponibles.'}
									</ProjectStatus>
								)}
							</ProjectCard>
						))}
					</ProjectsGrid>
				</ProjectsViewport>
			)}
			{!isFetching && moduleItemId && !projects.length && (
				<StatusMessage role='status'>
					No hay proyectos o líneas de negocio disponibles.
				</StatusMessage>
			)}
		</AssignmentsContainer>
	);
};

export default ContractorProjectAssignments;
